import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { X, Loader2, Receipt, Sparkles, Download, RefreshCw, CheckCircle2, Building2, Save } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import Portal from "./Portal";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const dmy = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const STATUS = {
  paid:    { label: "Paid",    cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pending: { label: "Pending", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  overdue: { label: "Overdue", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  active:  { label: "Active",  cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  draft:   { label: "Setup",   cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function SubscriptionsDialog({ onClose }) {
  const [tab, setTab] = useState("subs"); // "subs" | "invoices"
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [busy, setBusy] = useState("");
  const [cfg, setCfg] = useState(null);

  const load = useCallback(async () => {
    try {
      const [subs, invs, billing] = await Promise.all([
        api.get("/pms/admin/subscriptions"),
        api.get("/pms/admin/subscriptions/invoices"),
        api.get("/pms/billing/config"),
      ]);
      setData(subs.data);
      setInvoices(invs.data.invoices);
      setCfg(billing.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const payWithRazorpay = async (order, invoiceId) => {
    return new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => {
        const rzp = new window.Razorpay({
          key: order.key_id, amount: order.amount, currency: order.currency,
          name: "Atiti Homestays", description: "PMS Subscription Renewal",
          order_id: order.order_id, prefill: order.prefill || {},
          theme: { color: "#4F46E5" },
          handler: async (r) => {
            try {
              await api.post(`/pms/admin/subscriptions/invoices/${invoiceId}/pay/verify`, {
                razorpay_order_id: r.razorpay_order_id,
                razorpay_payment_id: r.razorpay_payment_id,
                razorpay_signature: r.razorpay_signature,
              });
              toast.success("Payment verified · subscription extended");
              resolve(true);
            } catch (e) { toast.error(errMsg(e)); reject(e); }
          },
          modal: { ondismiss: () => reject(new Error("dismissed")) },
        });
        rzp.open();
      };
      s.onerror = () => reject(new Error("checkout script failed"));
      document.body.appendChild(s);
    });
  };

  const renew = async (row) => {
    setBusy(row.property_id);
    try {
      const { data: order } = await api.post(`/pms/admin/subscriptions/${row.property_id}/renew`, { force: true });
      if (order.mode === "razorpay") {
        await payWithRazorpay(order, order.invoice.id);
      } else {
        toast.info(`Mock payment · invoice ${order.invoice.invoice_no} created`);
      }
      await load();
    } catch (e) {
      if (e?.message !== "dismissed") toast.error(errMsg(e));
    }
    setBusy("");
  };

  const downloadPdf = async (inv) => {
    try {
      const r = await api.get(`/pms/admin/subscriptions/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.invoice_no}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Portal>
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[80] grid place-items-center p-4 sm:p-6 overflow-y-auto" onClick={onClose}>
        <div data-testid="subs-dialog" className="bg-white rounded-2xl w-[96vw] min-h-[92vh] max-h-[92vh] max-w-none shadow-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <div className="border-b border-slate-100 px-6 py-4 flex items-center gap-4 shrink-0">
            <Receipt size={22} className="text-[#4F46E5]" />
            <div className="flex-1">
              <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-400">Admin · Cross-property control</p>
              <h2 className="font-display text-xl font-bold text-slate-900">Subscription &amp; Invoices</h2>
            </div>
            <button data-testid="subs-refresh" onClick={load} className="text-slate-500 hover:text-slate-900 flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200 hover:border-slate-400">
              <RefreshCw size={13} /> Refresh
            </button>
            <button data-testid="subs-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
          </div>

          {!data ? (
            <div className="flex-1 flex items-center justify-center"><Loader2 className="animate-spin text-slate-400" size={28} /></div>
          ) : (
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Info banner */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 flex items-start gap-3">
                <Sparkles size={16} className="mt-0.5 text-[#4F46E5] shrink-0" />
                <div className="text-[12px] text-indigo-900 leading-relaxed space-y-1.5">
                  <p>
                    <b>How PMS billing works.</b> Every property starts with a one-time <b>setup payment</b> that activates its
                    PMS workspace. From then on, this screen keeps the collection going every 30 days — you can pay yourself
                    from here, and we email a reminder <b>the moment an invoice is raised, 2 days before it's due, and again if it's overdue</b>.
                  </p>
                  <p>
                    Every Atiti OTA listing you map to a PMS unit on an <b>active</b> property is billed here at
                    <b> ₹{data.plan_rate}/unit/month</b> (SAC {data.sac_code}, +{Math.round(data.gst_rate * 100)}% GST). Those listings are
                    automatically excluded from OTA billing — a "Billed via PMS · ₹0" pill shows on the OTA host dashboard instead,
                    so you never pay twice for the same room.
                  </p>
                </div>
              </div>

              {/* KPI cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { k: "Total MRR", v: `₹ ${fmt(data.rollup.total_mrr)}`, hint: "Sum of all active subscriptions" },
                  { k: "Active Units", v: data.rollup.total_units, hint: "OTA listings mapped and billed here" },
                  { k: "Properties Live", v: data.rollup.properties_live, hint: "Active PMS subscriptions" },
                  { k: "Outstanding", v: `₹ ${fmt(data.rollup.outstanding)}`, hint: "Pending renewal invoices" },
                ].map((t) => (
                  <div key={t.k} data-testid={`subs-kpi-${t.k.replace(/\s+/g, "-").toLowerCase()}`} className="bg-white rounded-2xl border border-slate-200 p-4">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{t.k}</p>
                    <p className="font-display text-2xl font-bold text-slate-900 mt-1">{t.v}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{t.hint}</p>
                  </div>
                ))}
              </div>

              {/* Tabs */}
              <div data-testid="subs-tabs" className="inline-flex bg-slate-100 rounded-full p-1 text-[12px] font-semibold">
                <button data-testid="subs-tab-subs" onClick={() => setTab("subs")}
                  className={`px-4 py-1.5 rounded-full transition-colors ${tab === "subs" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>Subscriptions ({data.properties.length})</button>
                <button data-testid="subs-tab-invoices" onClick={() => setTab("invoices")}
                  className={`px-4 py-1.5 rounded-full transition-colors ${tab === "invoices" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>Invoices ({(invoices || []).length})</button>
                <button data-testid="subs-tab-business" onClick={() => setTab("business")}
                  className={`px-4 py-1.5 rounded-full transition-colors ${tab === "business" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>Business &amp; GST</button>
              </div>

              {tab === "business" && <BusinessGstPanel />}

              {tab === "subs" && (
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100 bg-slate-50">
                        <th className="text-left px-4 py-3 font-semibold">Property</th>
                        <th className="text-left font-semibold">Status</th>
                        <th className="text-right font-semibold">Units billed</th>
                        <th className="text-right font-semibold">Rate</th>
                        <th className="text-right font-semibold">Monthly</th>
                        <th className="text-left font-semibold pl-4">Cycle</th>
                        <th className="text-left font-semibold">Next renewal</th>
                        <th className="text-right px-4 font-semibold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.properties.map((r) => {
                        const st = STATUS[r.status] || STATUS.draft;
                        return (
                          <tr key={r.property_id} data-testid={`subs-row-${r.property_id}`} className="border-b border-slate-50 last:border-0">
                            <td className="px-4 py-3">
                              <p className="font-semibold text-slate-900">{r.property_name}</p>
                              <p className="text-[10px] text-slate-400">{r.city || "—"}</p>
                            </td>
                            <td>
                              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                            </td>
                            <td className="text-right font-mono">
                              {r.unit_count}
                              {r.status === "active" && r.live_unit_count !== r.unit_count && (
                                <span className="ml-1 text-[10px] text-amber-600" title="Live count differs — takes effect next cycle">→ {r.live_unit_count}</span>
                              )}
                            </td>
                            <td className="text-right font-mono text-slate-500">₹{r.plan_rate}</td>
                            <td className="text-right font-mono font-bold text-slate-900">₹ {fmt(r.monthly_amount)}</td>
                            <td className="pl-4 text-slate-500">
                              {r.cycle_start ? `${dmy(r.cycle_start)} → ${dmy(r.cycle_end)}` : "—"}
                            </td>
                            <td className="text-slate-500">{dmy(r.next_renewal)}</td>
                            <td className="text-right px-4">
                              {r.status === "active" ? (
                                <button data-testid={`subs-pay-${r.property_id}`} onClick={() => renew(r)} disabled={busy === r.property_id}
                                  className="btn-brand rounded-full px-4 py-1.5 text-[11px] font-semibold flex items-center gap-1.5 ml-auto disabled:opacity-50">
                                  {busy === r.property_id ? <Loader2 className="animate-spin" size={11} /> : r.pending_invoice_id ? <CheckCircle2 size={11} /> : <RefreshCw size={11} />}
                                  {r.pending_invoice_id ? "Pay now" : "Renew early"}
                                </button>
                              ) : (
                                <span className="text-[10px] text-slate-400">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {data.properties.length === 0 && (
                        <tr><td colSpan="8" className="px-6 py-12 text-center text-sm text-slate-400">No properties yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {tab === "invoices" && (
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100 bg-slate-50">
                        <th className="text-left px-4 py-3 font-semibold">Invoice No</th>
                        <th className="text-left font-semibold">Property</th>
                        <th className="text-left font-semibold">Kind</th>
                        <th className="text-left font-semibold">Issued</th>
                        <th className="text-left font-semibold">Cycle</th>
                        <th className="text-right font-semibold">Units</th>
                        <th className="text-right font-semibold">Total</th>
                        <th className="text-left font-semibold pl-3">Status</th>
                        <th className="text-right px-4 font-semibold">PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(invoices || []).map((inv) => {
                        const st = STATUS[inv.status] || STATUS.pending;
                        return (
                          <tr key={inv.id} data-testid={`inv-row-${inv.id}`} className="border-b border-slate-50 last:border-0">
                            <td className="px-4 py-3 font-mono text-slate-900 font-semibold">{inv.invoice_no}</td>
                            <td>{inv.property_name}</td>
                            <td className="capitalize text-slate-500">{inv.kind || "setup"}</td>
                            <td className="text-slate-500">{dmy(inv.issued_at)}</td>
                            <td className="text-slate-500">{inv.cycle_start ? `${dmy(inv.cycle_start)} → ${dmy(inv.cycle_end)}` : "—"}</td>
                            <td className="text-right font-mono">{inv.unit_count ?? "—"}</td>
                            <td className="text-right font-mono font-bold text-slate-900">₹ {fmt(inv.total)}</td>
                            <td className="pl-3">
                              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                            </td>
                            <td className="text-right px-4">
                              <button data-testid={`inv-pdf-${inv.id}`} onClick={() => downloadPdf(inv)}
                                className="text-[#4F46E5] hover:bg-indigo-50 rounded-full p-1.5">
                                <Download size={13} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      {(invoices || []).length === 0 && (
                        <tr><td colSpan="9" className="px-6 py-12 text-center text-sm text-slate-400">No invoices yet. They appear here on setup, renewal or when you tap "Pay now".</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {cfg && !cfg.enabled && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Razorpay is not configured on this instance — "Pay now" will run in mock mode and mark the invoice paid for demo purposes.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
}

function BusinessGstPanel() {
  const [buyer, setBuyer] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try {
      const b = await api.get("/pms/admin/subscriptions/buyer-profile");
      setBuyer(b.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async (kind) => {
    setBusy(kind);
    try {
      const r = await api.put(`/pms/admin/subscriptions/${kind}-profile`, buyer);
      setBuyer(r.data);
      toast.success("Your business details saved");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  if (!buyer) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-300" size={22} /></div>;

  const F = ({ label, value, onChange, testId, cols = 1 }) => (
    <label className={`block text-xs ${cols === 2 ? "col-span-2" : ""}`}>
      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</span>
      <input data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" />
    </label>
  );

  const Card = ({ title, subtitle, profile, setProfile, kind }) => (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
      <div className="flex items-start gap-3">
        <Building2 size={20} className="text-[#4F46E5]" />
        <div className="flex-1">
          <h3 className="font-display font-bold text-slate-900">{title}</h3>
          <p className="text-[11px] text-slate-500">{subtitle}</p>
        </div>
        <button data-testid={`${kind}-save`} onClick={() => save(kind)} disabled={busy === kind}
          className="btn-brand rounded-full px-4 py-1.5 text-[11px] font-semibold flex items-center gap-1.5 disabled:opacity-50">
          {busy === kind ? <Loader2 className="animate-spin" size={11} /> : <Save size={11} />} Save
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <F label="Business Name*" value={profile.business_name} testId={`${kind}-business-name`}
          onChange={(v) => setProfile({ ...profile, business_name: v })} cols={2} />
        <F label="GSTIN" value={profile.gstin} testId={`${kind}-gstin`}
          onChange={(v) => setProfile({ ...profile, gstin: v.toUpperCase() })} />
        <F label="PAN" value={profile.pan} testId={`${kind}-pan`}
          onChange={(v) => setProfile({ ...profile, pan: v.toUpperCase() })} />
        <F label="Address Line 1" value={profile.address_line1} testId={`${kind}-addr1`}
          onChange={(v) => setProfile({ ...profile, address_line1: v })} cols={2} />
        <F label="Address Line 2" value={profile.address_line2} testId={`${kind}-addr2`}
          onChange={(v) => setProfile({ ...profile, address_line2: v })} cols={2} />
        <F label="City" value={profile.city} testId={`${kind}-city`}
          onChange={(v) => setProfile({ ...profile, city: v })} />
        <F label="State" value={profile.state} testId={`${kind}-state`}
          onChange={(v) => setProfile({ ...profile, state: v })} />
        <F label="State Code" value={profile.state_code} testId={`${kind}-state-code`}
          onChange={(v) => setProfile({ ...profile, state_code: v })} />
        <F label="Postal Code" value={profile.postal_code} testId={`${kind}-postal`}
          onChange={(v) => setProfile({ ...profile, postal_code: v })} />
        <F label="Email" value={profile.email} testId={`${kind}-email`}
          onChange={(v) => setProfile({ ...profile, email: v })} />
        <F label="Phone" value={profile.phone} testId={`${kind}-phone`}
          onChange={(v) => setProfile({ ...profile, phone: v })} />
        {kind === "seller" && (
          <F label="Logo URL" value={profile.logo_url} testId="seller-logo" cols={2}
            onChange={(v) => setProfile({ ...profile, logo_url: v })} />
        )}
      </div>
      <p className="text-[10px] text-slate-400 leading-relaxed">
        Your business details — appear on every Atiti PMS tax invoice as the Bill-To recipient. Same state as Atiti (Bihar) → CGST + SGST split · different state → IGST 18%.
      </p>
    </div>
  );

  return (
    <div data-testid="business-gst-panel" className="max-w-3xl">
      <Card title="Your Business Details (Bill To)" subtitle="Appears as the Bill-To recipient on every Atiti PMS tax invoice we issue to you"
        profile={buyer} setProfile={setBuyer} kind="buyer" />
      <p className="text-[11px] text-slate-400 mt-4 leading-relaxed">
        Atiti's seller details (business name, GSTIN, address, logo) are managed centrally by Atiti Homestays Private Limited and appear on every invoice automatically — nothing you need to fill on that side.
      </p>
    </div>
  );
}
